/**
 * PairingQuiz Student Renderer
 * Student-style UI with left-right pairing, SVG line connections,
 * 3D shadows, bright colors, and Lottie animations.
 * Uses usePairingQuiz headless hook for logic.
 */

import { cn } from '../../../lib/utils'
import { KatexText, SubmitButton, QuizSolveDescription, QuizHeader, QuizResultToggle, type QuizResultToggleValue } from '../shared'
import type { QuizSolveResult } from '../../../types/legacyQuiz'
import type { PairingQuizData } from '../hooks'
import { usePairingQuiz } from '../hooks'
import { useTranslation } from 'react-i18next'
import { X } from 'lucide-react'
import Lottie from 'lottie-react'
import lineAnimation from '../pairing/assets/line-effect.json'
import dotAnimation from '../pairing/assets/dot-effect.json'

// Legacy line colors
const LINE_COLOR_DEFAULT = '#55C3FF'

interface PairingStudentProps {
  quizId: string | number
  quiz: PairingQuizData
  onSolve?: (quizId: string | number, answers: string[], vars: Record<string, unknown>, elapsedTime: number) => void
  solveResult?: QuizSolveResult | null
  showReallyTrue?: boolean
  isViewSolved?: boolean
  initialAnswers?: string[]
  submitLabel?: string
  hideSubmitButton?: boolean
  onAnswerChange?: (answers: string[]) => void
  disableSound?: boolean
  difficultyLevel?: number | null
  /** G3 — true면 풀이 후 해설(QuizSolveDescription)을 숨긴다(정오답 피드백은 유지). 기본 false=현행 노출. */
  hideExplanation?: boolean
  /** true면 결과 토글은 유지하되 풀이/해설 UI만 숨긴다. */
  hideSolveDescription?: boolean
  /** G4 — true면 유형별 노출표에 따라 발문/문제 이미지를 흐린다. */
  dimChoiceText?: boolean
}

export function PairingStudent({
  quizId,
  quiz,
  onSolve,
  solveResult,
  showReallyTrue = false,
  isViewSolved = false,
  initialAnswers: initialAnswersProp,
  submitLabel,
  hideSubmitButton = false,
  onAnswerChange,
  disableSound: _disableSound = false,
  difficultyLevel,
  hideExplanation = false,
  hideSolveDescription = false,
  dimChoiceText = false,
}: PairingStudentProps) {
  const { t } = useTranslation('quiz')

  const {
    displayPairings,
    selected,
    lines,
    previewLine,
    hoveredPos,
    setHoveredPos,
    hasAnyAnswer,
    isSolved,
    canSubmit,
    shouldShowCorrectAnswers,
    isShowingCorrectAnswers,
    setIsShowingCorrectAnswers,
    pairs,
    containerRef,
    leftRefs,
    rightRefs,
    handleSelect,
    handleClearPairByLeft,
    handleClearPairByRight,
    handleSubmit,
    recalcLines,
    getLineColor,
  } = usePairingQuiz({
    quizId,
    quiz,
    onSolve,
    solveResult,
    showReallyTrue,
    isViewSolved,
    initialAnswers: initialAnswersProp,
    onAnswerChange,
  })

  return (
    <div className="space-y-6">
      {/* 문제 헤더 */}
      <QuizHeader
        quizId={quizId}
        typeLabel={t('types.pairing.label')}
        title={quiz.title}
        difficultyLevel={difficultyLevel}
        variant="student"
        image={quiz.image}
        imageMobile={quiz.imageMobile}
        desmos={quiz.desmos}
        vars={quiz.vars}
        dimTitleText={dimChoiceText}
        dimMedia={dimChoiceText}
      />

      {/* Pairing area: left column + SVG lines + right column */}
      <div ref={containerRef} className="relative flex min-w-0">
        {/* Left column */}
        <div className="z-10 flex min-w-0 basis-[208px] flex-col md:basis-[296px]">
          {pairs.left.map((item, index) => {
            const pairing = displayPairings[index]
            const isPaired = pairing !== null
            const isCurrent = selected?.pos === 'left' && selected?.index === index
            const isPending = selected !== null && selected.pos !== 'left'
            const isHovered = hoveredPos?.pos === 'left' && hoveredPos?.index === index
            const pairCorrect = isSolved ? solveResult?.corrects?.[index] : undefined

            const isTrue = isSolved && (shouldShowCorrectAnswers || pairCorrect === true)
            const isFalse = isSolved && !shouldShowCorrectAnswers && pairCorrect === false && isPaired

            return (
              <div
                key={`left-${index}`}
                className={cn('relative min-w-0 cursor-pointer flex items-center mb-4 z-10', {
                  'pointer-events-none': isSolved || isViewSolved,
                })}
                onMouseEnter={() => setHoveredPos({ pos: 'left', index })}
                onMouseLeave={() => setHoveredPos(null)}
              >
                {/* Box */}
                <div
                  ref={el => { leftRefs.current[index] = el }}
                  className={cn(
                    'w-full min-w-0 gap-2 min-h-[96px] border-2 border-gray-400 text-[20px] font-medium text-gray-900 rounded-3xl p-4 flex justify-center items-center',
                    '[box-shadow:0px_4px_0px_0px_rgba(0,0,0,0.10)]',
                    (isHovered || isPending) && !isCurrent && !isTrue && !isFalse &&
                      'border-lc-lightblue-900 text-lc-lightblue-900 [box-shadow:0px_4px_0px_0px_rgba(85,195,255,0.30)]',
                    isCurrent && 'text-lc-lightblue-dark bg-lc-lightblue-100 border-lc-lightblue-900 [box-shadow:unset]',
                    isTrue && 'border-lc-mint-900 bg-lc-mint-100 text-lc-mint-dark [box-shadow:unset]',
                    isFalse && 'border-lc-red-900 bg-lc-red-100 text-lc-red-900 [box-shadow:unset]',
                  )}
                  onClick={() => handleSelect('left', index)}
                >
                  {item.image?.signedUrl && (
                    <img
                      src={item.image.signedUrl}
                      alt="pairing"
                      onLoad={recalcLines}
                      className={cn('min-w-0 max-w-full object-contain', dimChoiceText && 'opacity-[0.05]')}
                    />
                  )}
                  {item.answer && (
                    <div data-quiz-choice-text className={cn('min-w-0 break-words text-center', dimChoiceText && 'opacity-[0.05]')}>
                      <KatexText text={item.answer} />
                    </div>
                  )}
                </div>

                {/* Dot connector */}
                <div
                  className={cn(
                    'group w-8 h-8 absolute -right-4 top-1/2 -translate-y-1/2 overflow-hidden bg-gray-100 text-gray-500 text-[16px] font-semibold border-2 border-gray-400 rounded-full flex items-center justify-center z-20 cursor-pointer',
                    (isHovered || isPending) && !isCurrent && !(isCurrent || isPaired) && !isTrue && !isFalse &&
                      'bg-lc-lightblue-200 border-lc-lightblue-900 text-lc-lightblue-900',
                    (isCurrent || isPaired) && !isTrue && !isFalse &&
                      'bg-lc-lightblue-900 border-transparent text-white',
                    isTrue && 'border-transparent bg-lc-mint-900 text-white',
                    isFalse && 'border-transparent bg-lc-red-900 text-white',
                  )}
                  onClick={(e) => {
                    e.stopPropagation()
                    if (isPaired) {
                      handleClearPairByLeft(index)
                    } else {
                      handleSelect('left', index)
                    }
                  }}
                >
                  {index + 1}
                  {/* X button on hover to clear */}
                  {isPaired && !isSolved && (
                    <X
                      className="hidden group-hover:block absolute w-6 h-6 text-white bg-lc-lightblue-900"
                      strokeWidth={3}
                    />
                  )}
                </div>

                {/* Lottie: line-effect */}
                <div
                  className={cn(
                    'absolute top-1/2 -translate-y-1/2 w-8 h-8 -right-12 hidden',
                    isCurrent && (!hoveredPos || hoveredPos.pos === 'left') && !hasAnyAnswer && 'block',
                  )}
                >
                  <Lottie animationData={lineAnimation} loop autoPlay style={{ width: '100%', height: '100%' }} />
                </div>

                {/* Lottie: dot-effect */}
                <div
                  className={cn(
                    'absolute top-1/2 -translate-y-1/2 w-10 h-10 -right-5 hidden',
                    isPending && !hasAnyAnswer && 'block',
                  )}
                >
                  <Lottie animationData={dotAnimation} loop autoPlay style={{ width: '100%', height: '100%' }} />
                </div>
              </div>
            )
          })}
        </div>

        {/* SVG Lines area */}
        <div className="relative min-w-10 flex-1" />

        {/* SVG overlay for lines */}
        <svg
          className="absolute inset-0 pointer-events-none z-0"
          style={{ width: '100%', height: '100%', overflow: 'visible' }}
        >
          {lines.map((line, i) => (
            <line
              key={i}
              x1={line.x1}
              y1={line.y1}
              x2={line.x2}
              y2={line.y2}
              stroke={getLineColor(line.leftIdx)}
              strokeWidth={3}
              strokeLinecap="round"
            />
          ))}
          {/* Dashed preview line */}
          {previewLine && (
            <line
              x1={previewLine.x1}
              y1={previewLine.y1}
              x2={previewLine.x2}
              y2={previewLine.y2}
              stroke={LINE_COLOR_DEFAULT}
              strokeWidth={3}
              strokeLinecap="round"
              strokeDasharray="6 6"
            />
          )}
        </svg>

        {/* Right column */}
        <div className="z-10 flex min-w-0 basis-[208px] flex-col md:basis-[296px]">
          {pairs.right.map((item, index) => {
            const isCurrent = selected?.pos === 'right' && selected?.index === index
            const isPending = selected !== null && selected.pos !== 'right'
            const isHovered = hoveredPos?.pos === 'right' && hoveredPos?.index === index

            const pairedLeftIdx = displayPairings.findIndex(p => p?.rightIndex === index)
            const isPairedByAnyLeft = pairedLeftIdx !== -1

            let isTrue = false
            let isFalse = false
            let dotNum: string | number = ''

            if (shouldShowCorrectAnswers) {
              const correctIdx = solveResult?.correctAnswers?.findIndex(key => key === item.key) ?? -1
              if (correctIdx !== -1) {
                isTrue = true
                dotNum = correctIdx + 1
              }
            } else if (isSolved && isPairedByAnyLeft) {
              const pairCorrect = solveResult?.corrects?.[pairedLeftIdx]
              isTrue = pairCorrect === true
              isFalse = pairCorrect === false
              dotNum = pairedLeftIdx + 1
            } else if (!isSolved && isPairedByAnyLeft) {
              dotNum = pairedLeftIdx + 1
            }

            return (
              <div
                key={`right-${index}`}
                className={cn('relative min-w-0 cursor-pointer flex items-center flex-row-reverse mb-4 z-10', {
                  'pointer-events-none': isSolved || isViewSolved,
                })}
                onMouseEnter={() => setHoveredPos({ pos: 'right', index })}
                onMouseLeave={() => setHoveredPos(null)}
              >
                {/* Box */}
                <div
                  ref={el => { rightRefs.current[index] = el }}
                  className={cn(
                    'w-full min-w-0 gap-2 min-h-[96px] border-2 border-gray-400 text-[20px] font-medium text-gray-900 rounded-3xl p-4 flex justify-center items-center',
                    '[box-shadow:0px_4px_0px_0px_rgba(0,0,0,0.10)]',
                    (isHovered || isPending) && !isCurrent && !isTrue && !isFalse &&
                      'border-lc-lightblue-900 text-lc-lightblue-900 [box-shadow:0px_4px_0px_0px_rgba(85,195,255,0.30)]',
                    isCurrent && 'text-lc-lightblue-dark bg-lc-lightblue-100 border-lc-lightblue-900 [box-shadow:unset]',
                    isTrue && 'border-lc-mint-900 bg-lc-mint-100 text-lc-mint-dark [box-shadow:unset]',
                    isFalse && 'border-lc-red-900 bg-lc-red-100 text-lc-red-900 [box-shadow:unset]',
                  )}
                  onClick={() => handleSelect('right', index)}
                >
                  {item.image?.signedUrl && (
                    <img
                      src={item.image.signedUrl}
                      alt="pairing"
                      onLoad={recalcLines}
                      className={cn('min-w-0 max-w-full object-contain', dimChoiceText && 'opacity-[0.05]')}
                    />
                  )}
                  {item.answer && (
                    <div data-quiz-choice-text className={cn('min-w-0 break-words text-center', dimChoiceText && 'opacity-[0.05]')}>
                      <KatexText text={item.answer} />
                    </div>
                  )}
                </div>

                {/* Dot connector */}
                <div
                  className={cn(
                    'group w-8 h-8 absolute -left-4 top-1/2 -translate-y-1/2 overflow-hidden bg-white text-gray-500 text-[16px] font-semibold border-2 border-gray-400 rounded-full flex items-center justify-center z-20 cursor-pointer',
                    (isPending) && !isCurrent && !(isCurrent || isPairedByAnyLeft) && !isTrue && !isFalse &&
                      'bg-lc-lightblue-200 border-lc-lightblue-900 text-lc-lightblue-900',
                    isHovered && !isCurrent && !(isCurrent || isPairedByAnyLeft) && !isTrue && !isFalse &&
                      'bg-white border-lc-lightblue-900 text-lc-lightblue-900',
                    (isCurrent || isPairedByAnyLeft) && !isTrue && !isFalse &&
                      'bg-lc-lightblue-900 border-transparent text-white',
                    isTrue && 'border-transparent bg-lc-mint-900 text-white',
                    isFalse && 'border-transparent bg-lc-red-900 text-white',
                  )}
                  onClick={(e) => {
                    e.stopPropagation()
                    if (isPairedByAnyLeft) {
                      handleClearPairByRight(index)
                    } else {
                      handleSelect('right', index)
                    }
                  }}
                >
                  {dotNum}
                  {/* X button on hover to clear */}
                  {isPairedByAnyLeft && !isSolved && (
                    <X
                      className="hidden group-hover:block absolute w-6 h-6 text-white bg-lc-lightblue-900"
                      strokeWidth={3}
                    />
                  )}
                </div>

                {/* Lottie: line-effect */}
                <div
                  className={cn(
                    'absolute top-1/2 -translate-y-1/2 w-8 h-8 -left-12 hidden',
                    isCurrent && (!hoveredPos || hoveredPos.pos === 'right') && !hasAnyAnswer && 'block',
                  )}
                >
                  <Lottie animationData={lineAnimation} loop autoPlay style={{ width: '100%', height: '100%' }} className="rotate-180" />
                </div>

                {/* Lottie: dot-effect */}
                <div
                  className={cn(
                    'absolute top-1/2 -translate-y-1/2 w-10 h-10 -left-5 hidden',
                    isPending && !hasAnyAnswer && 'block',
                  )}
                >
                  <Lottie animationData={dotAnimation} loop autoPlay style={{ width: '100%', height: '100%' }} />
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {/* Submit button */}
      {!isSolved && !isViewSolved && !hideSubmitButton && (
        <SubmitButton
          onClick={handleSubmit}
          disabled={!canSubmit}
          label={submitLabel ?? (canSubmit ? t('common.checkAnswer') : t('types.pairing.matchPairs'))}
        />
      )}

      {/* 풀이 결과 토글 (정답과 풀이 / 내가 쓴 답) */}
      {isSolved && !hideExplanation && (
        <QuizResultToggle
          value={isShowingCorrectAnswers ? 'correct-and-explanation' : 'my-answer'}
          onChange={(next: QuizResultToggleValue) => setIsShowingCorrectAnswers(next === 'correct-and-explanation')}
        />
      )}

      {/* 해설은 '정답과 풀이' 일 때만 노출 */}
      {isSolved && isShowingCorrectAnswers && !hideExplanation && !hideSolveDescription && (
        <QuizSolveDescription
          description={solveResult?.description ?? undefined}
          commentaryImages={quiz.commentaryImages}
          vars={quiz.vars}
          id={`${quizId}-desc`}
          actionVisible={false}
        />
      )}
    </div>
  )
}
